import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { CommonPopover } from '../common-popover/common-popover';
import { CommonPopoverContext } from '../common-popover/common-popover.context';
import { DefaultClosePopoverButton } from '../common-popover/default-close-popover-button';
import { Component } from '../../../infrastructure/type-extensions';
import { IMultiSelectClosedPropsDerivedFromItemsList } from './make-multi-select-in-popover';
import { MultiSelectOption } from '../multi-select-list/_multi-select-option';
import { IMeetingPresenterGroupDTO } from '../../../api/custom-pages.dao';
import './make-multi-select.scss';

type PresenterGroupsMultiSelectProps = {
    items: { meetingPresenterGroups: IMeetingPresenterGroupDTO[] };
    applyButtonText: React.ReactNode;
    resetButtonText: React.ReactNode;
    onChange: (value: string[]) => void;
    value?: string[];
    position?: 'left' | 'right' | 'top' | 'bottom';
    align?: 'start' | 'center' | 'end';
};

type PresenterOption = {
    key: string;
    meetingIds: string[];
    presenterNames: string[];
};

const makePresenterGroupsMultiSelect = <T extends object>(
    ComponentWhichTriggerSingleSelection: Component<T & IMultiSelectClosedPropsDerivedFromItemsList>
) => {
    const ComponentWithForwardedRef = React.forwardRef<HTMLElement, T>((props: T, ref: React.Ref<HTMLElement>) => {
        return (
            <CommonPopoverContext.Consumer>
                {context => <ComponentWhichTriggerSingleSelection {...props} isOpen={context.isOpen} ref={ref} />}
            </CommonPopoverContext.Consumer>
        );
    });

    return (props: PresenterGroupsMultiSelectProps & T) => {
        const {
            items,
            applyButtonText,
            resetButtonText,
            onChange,
            position,
            align
        } = props;

        const valueList = Array.isArray(props.value) ? props.value : [];
        const valueKey = valueList.join('|');
        const [selectedMeetingIds, setSelectedMeetingIds] = useState<string[]>(valueList);
        const [initialSelectedMeetingIds, setInitialSelectedMeetingIds] = useState<string[]>(valueList);

        useEffect(() => {
            setInitialSelectedMeetingIds(valueList);
            setSelectedMeetingIds(valueList);
        }, [valueKey]);

        const presenterOptions: PresenterOption[] = (() => {
            const byPresenters = new Map<string, PresenterOption>();
            (items?.meetingPresenterGroups || []).forEach(group => {
                const presenters = group.presenterNames || [];
                const presentersKey = presenters.join(', ');
                const meetingId = String(group.meetingId);
                const existing = byPresenters.get(presentersKey);
                if (existing) {
                    existing.meetingIds.push(meetingId);
                } else {
                    byPresenters.set(presentersKey, {
                        key: presentersKey,
                        meetingIds: [meetingId],
                        presenterNames: presenters
                    });
                }
            });
            return Array.from(byPresenters.values());
        })();

        const isSelected = (meetingIds: string[]) => meetingIds.some(id => selectedMeetingIds.includes(id));
        const isInitiallySelected = (meetingIds: string[]) => meetingIds.some(id => initialSelectedMeetingIds.includes(id));

        const onToggle = (meetingIds: string[]) => {
            if (meetingIds.some(id => selectedMeetingIds.includes(id))) {
                setSelectedMeetingIds(selectedMeetingIds.filter(id => !meetingIds.includes(id)));
            } else {
                setSelectedMeetingIds([...selectedMeetingIds, ...meetingIds]);
            }
        };

        const resetToDefault = () => {
            setSelectedMeetingIds(initialSelectedMeetingIds);
        };

        const handleSave = () => {
            onChange(selectedMeetingIds);
            resetToDefault();
        };
        return (
            <CommonPopover
                body={presenterOptions.length === 0 ? (
                    <div className='single-select-no-content'>
                        <FormattedMessage
                            id='oe.items.no'
                            description='Label text'
                            defaultMessage='No items'
                        />
                    </div>
                ) : (
                    <div className='multi-select-list'>
                        {presenterOptions.map(option => (
                            <MultiSelectOption
                                key={option.key}
                                isSelected={isSelected(option.meetingIds)}
                                isDisabled={isInitiallySelected(option.meetingIds)}
                                onClick={() => onToggle(option.meetingIds)}
                                name={option.presenterNames.join(', ')}
                                hoverLabel={option.presenterNames.join(', ')}
                            />
                        ))}
                    </div>
                )}
                footer={(
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultClosePopoverButton
                            text={applyButtonText}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => handleSave()}
                        />
                        <DefaultClosePopoverButton
                            onClick={() => resetToDefault()}
                            text={resetButtonText}
                        />
                    </div>
                )}
                position={position || 'left'}
                align={align || 'start'}
                onClickOutside={resetToDefault}
            >
                <ComponentWithForwardedRef {...props} />
            </CommonPopover>
        );
    };
};

export { makePresenterGroupsMultiSelect, PresenterGroupsMultiSelectProps };
